import Container from "@mui/material/Container";
import {Grid, IconButton, Link, ThemeProvider} from "@mui/material";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import genie_logo from "../../../assets/images/logo.svg";
import * as React from "react";
import {useEffect, useMemo, useState} from "react";
import useSocialMedia from "../../../hooks/useSocialMedia";
import ReactGA from "react-ga4";
import FabLanguageDropdown from "../../shared/language-dropdown/FabLanguageDropdown";
import TextField from "@mui/material/TextField";
import {whiteInputsTheme} from "../../../util/theme/whiteInputsTheme";
import LoadingButton from "@mui/lab/LoadingButton";
import {validateEmail} from "../../../util/regex";
import UserService from "../../../api/userService";
import {toastError, toastSuccess} from "../../shared/toast/Toast";
import {getSafeBackendErrorMessage} from "../../../util/utils";
import {RootState, useAppSelector} from "../../../store";
import useCountdown from "../../../hooks/useCountdown";
import TimeUntilStartGrid from "../../wishes/display-wishes/common/time-until-start-grid/TimeUntilStartGrid";
import {Helmet} from "react-helmet";
import {StorageConstants} from "../../../util/constants";

const ComingSoon = () => {
    const {t} = useTranslation();
    const {socialMedia} = useSocialMedia();
    const [email, setEmail] = useState<string>("");
    const [loading, setLoading] = useState(false);
    const serverTime = useAppSelector((state: RootState) => state.appReducer.serverTime);

    // --- Countdown target: Dec 1, 00:00 (this year) ---
    const secondsToDec1 = useMemo(() => {
        const now = serverTime ? new Date(serverTime) : new Date();
        const target = new Date(now.getFullYear(), 11, 1, 0, 0, 0, 0);
        const diffMs = target.getTime() - now.getTime();
        return Math.max(0, Math.ceil(diffMs / 1000));
    }, [serverTime]);

    const onExpire = React.useCallback(() => {
        try {
            sessionStorage.removeItem(StorageConstants.FEATURE_TOGGLES);
        } catch (_) {
        }
        window.setTimeout(() => window.location.reload(), 5000);
    }, []);

    const isActive = secondsToDec1 > 0;
    const {left} = useCountdown(secondsToDec1, isActive, onExpire);

    useEffect(() => {
        if (secondsToDec1 === 0) onExpire();
    }, [secondsToDec1, onExpire]);

    const countDownMs = left > 0 ? left * 1000 : 0;

    const handleOnSocialMediaClick = (label: string) => {
        ReactGA.event({
            category: 'Social Links',
            action: 'social_link_click',
            label,
        });
    };

    const handleOnSubmitClick = () => {
        setLoading(true);
        UserService.subscribeNewsletter(email)
            .then(res => {
                toastSuccess(res.data.message);
                setEmail("");
            })
            .catch(e => {
                toastError(getSafeBackendErrorMessage(e.response?.data?.detail));
            })
            .finally(() => setLoading(false));
    };

    return (
        <Container component="main" maxWidth="lg"
                   sx={{
                       display: 'flex',
                       flexDirection: 'column',
                       flexGrow: 1,
                       alignItems: 'center',
                       justifyContent: 'center'
                   }}>
            <Helmet>
                <title>Genie</title>
            </Helmet>
            <img src={genie_logo} width={300} alt="genie-logo"/>

            <Grid container justifyContent="center" sx={{mt: 2, width: 'fit-content'}}>
                <TimeUntilStartGrid
                    recommended={false}
                    countDown={countDownMs}
                    drawStarted={false}
                    comingSoon
                />
            </Grid>

            <Typography variant="h2" color="primary" fontWeight={600} textAlign="center" sx={{
                fontSize: {xs: "2rem", md: '3.4rem'}
            }} mt={1.5} mb={1}>
                {t('coming_soon_title')}
            </Typography>

            <Typography variant="h6" color="primary" fontWeight={600} textAlign="center" sx={{
                fontSize: {xs: "1rem", md: '1.25rem'}
            }}>
                {t('coming_soon_subtitle')}
            </Typography>

            <Grid container justifyContent="center" alignItems="center" gap={1} mt={2} mb={1}>
                <Grid item xs={8} md={4}>
                    <ThemeProvider theme={whiteInputsTheme}>
                        <TextField
                            fullWidth
                            id="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            label={t('e-mail')}
                            name="email"
                            autoComplete="email"
                            color="primary"
                        />
                    </ThemeProvider>
                </Grid>
                <LoadingButton
                    loading={loading}
                    variant='contained'
                    color='primary'
                    size="large"
                    disabled={!validateEmail(email)}
                    onClick={handleOnSubmitClick}
                >
                    {t('submit')}
                </LoadingButton>
            </Grid>

            <Grid container justifyContent='center' sx={{my: 1}}>
                {socialMedia.map((sm) => (
                    <IconButton
                        key={sm.label}
                        color='primary'
                        onClick={() => handleOnSocialMediaClick(sm.label)}
                        component={Link}
                        href={sm.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="large"
                    >
                        {sm.icon}
                    </IconButton>
                ))}
            </Grid>
            <FabLanguageDropdown/>
        </Container>
    );
};
export default ComingSoon;
